import { Popover } from "@base-ui/react/popover"
import { usePortalContainer } from "@src/hooks/usePortalContainer"
import { ArrowIcon } from "@src/icons"
import { cn } from "@src/utils/cn"
import {
  type ComponentProps,
  type ReactElement,
  type ReactNode,
  cloneElement,
} from "react"

const InfoPopover = ({ children }: { children: ReactNode }) => (
  <Popover.Root>{children}</Popover.Root>
)

const InfoPopoverTrigger = ({
  children,
  // Triggers commonly wrap non-button hosts (dt, span, IconButton). Base UI
  // defaults nativeButton=true and throws a console error overlay that blocks
  // Playwright clicks when the host is not a native <button>.
  nativeButton = false,
  ...triggerProps
}: Omit<ComponentProps<typeof Popover.Trigger>, "render"> & {
  children: ReactElement
}) => (
  <Popover.Trigger
    {...triggerProps}
    nativeButton={nativeButton}
    render={({ children: _, ...props }) => cloneElement(children, props)}
  />
)

type InfoPopoverContentProps = {
  children: ReactNode
  className?: string
  side?: "top" | "bottom" | "left" | "right"
  sideOffset?: number
}

const InfoPopoverContent = ({
  children,
  className,
  side = "bottom",
  sideOffset = 8,
}: InfoPopoverContentProps) => {
  const portalContainer = usePortalContainer()

  return (
    <Popover.Portal container={portalContainer}>
      <Popover.Positioner
        collisionPadding={8}
        side={side}
        sideOffset={sideOffset}
      >
        <Popover.Popup
          className={cn(
            "-outline-offset-1 origin-(--transform-origin) rounded-xl bg-gray-800 px-2 py-2 font-semibold text-white text-xs shadow-lg outline-1 outline-white/5",
            "dark:bg-gray-950 dark:shadow-none dark:outline-white/10",
            "transition duration-100 ease-out",
            "data-starting-style:scale-95 data-starting-style:opacity-0",
            "data-ending-style:scale-95 data-ending-style:opacity-0 data-ending-style:duration-75 data-ending-style:ease-in",
            className
          )}
        >
          {children}
          <Popover.Arrow className="data-[side=right]:-rotate-90 data-[side=bottom]:-top-2 data-[side=left]:-right-2.5 data-[side=right]:-left-2.5 text-gray-800 data-[side=top]:bottom-2 data-[side=left]:rotate-90 data-[side=top]:rotate-180 dark:text-gray-950">
            <ArrowIcon className="h-3 w-5 shrink-0" />
          </Popover.Arrow>
        </Popover.Popup>
      </Popover.Positioner>
    </Popover.Portal>
  )
}

InfoPopover.Trigger = InfoPopoverTrigger
InfoPopover.Content = InfoPopoverContent

export default InfoPopover
